import * as G6 from '@antv/g6';
import { throttle } from 'lodash/fp';

import Behavior from 'dashboard/components/GraphCanvasV2/behaviors/behavior';
import NodeMouseDownEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-down-event';
import NodeMouseMoveEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-move-event';
import NodeMouseUpEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-up-event';
import NodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/node-position-change';
import SelectionBoxPositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-position-change-event';

import CollapsedGroupMouseDownEvent from '../events/collapsed-group-mouse-down-event';
import CollapsedGroupMouseMoveEvent from '../events/collapsed-group-mouse-move-event';
import CollapsedGroupMouseUpEvent from '../events/collapsed-group-mouse-up-event';
import CollapsedGroupPositionChangeEvent from '../events/collapsed-group-position-change';
import CollapsedPairwiseNodeMouseDownEvent from '../events/collapsed-pairwise-node-mouse-down-event';
import CollapsedPairwiseNodeMouseMoveEvent from '../events/collapsed-pairwise-node-mouse-move-event';
import CollapsedPairwiseNodeMouseUpEvent from '../events/collapsed-pairwise-node-mouse-up-event';
import CollapsedPairwiseNodePositionChangeEvent from '../events/collapsed-pairwise-node-position-change';
import {
  g6IdToCollapsedGroupId,
  g6IdToCollapsedPairwiseNodeId,
  g6IdToHashableNodeId,
} from '../G6Mapper';

interface DragOriginPoint {
  x: number;
  y: number;
}

export default class CanvasSelectablesDragBehavior extends Behavior {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private dragOrigin: DragOriginPoint;

  public get events() {
    return {
      [NodeMouseDownEvent.type]: 'handleNodeMouseDown',
      [NodeMouseMoveEvent.type]: 'handleNodeMouseMove',
      [NodeMouseUpEvent.type]: 'handleNodeMouseUp',
      [CollapsedGroupMouseDownEvent.type]: 'handleCollapsedGroupMouseDown',
      [CollapsedGroupMouseMoveEvent.type]: 'handleCollapsedGroupMouseMove',
      [CollapsedGroupMouseUpEvent.type]: 'handleCollapsedGroupMouseUp',
      [CollapsedPairwiseNodeMouseDownEvent.type]: 'handleCollapsedPairwiseNodeMouseDown',
      [CollapsedPairwiseNodeMouseMoveEvent.type]: 'handleCollapsedPairwiseNodeMouseMove',
      [CollapsedPairwiseNodeMouseUpEvent.type]: 'handleCollapsedPairwiseNodeMouseUp',
    };
  }

  public reactivate() {
    return;
  }

  public reset() {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragOrigin = null;
  }

  handleNodeMouseDown(e: NodeMouseDownEvent) {
    this.handleSelectableMouseDown(e);
  }

  handleCollapsedGroupMouseDown(e: CollapsedGroupMouseDownEvent) {
    this.handleSelectableMouseDown(e);
  }

  handleCollapsedPairwiseNodeMouseDown(e: CollapsedPairwiseNodeMouseMoveEvent) {
    this.handleSelectableMouseDown(e);
  }

  handleSelectableMouseDown(
    e: NodeMouseDownEvent | CollapsedGroupMouseDownEvent | CollapsedPairwiseNodeMouseMoveEvent,
  ) {
    const worldPoint = this.g6Wrapper.getPointByClient(e.raw.x, e.raw.y);
    this.dragOrigin = { x: worldPoint.x, y: worldPoint.y };
  }

  handleNodeMouseMove(e: NodeMouseMoveEvent) {
    this.handleSelectableMouseMove(e);
  }

  handleCollapsedGroupMouseMove(e: CollapsedGroupMouseMoveEvent) {
    this.handleSelectableMouseMove(e);
  }

  handleCollapsedPairwiseNodeMouseMove(e: CollapsedPairwiseNodeMouseMoveEvent) {
    this.handleSelectableMouseMove(e);
  }

  handleSelectableMouseMove(
    e: NodeMouseMoveEvent | CollapsedGroupMouseMoveEvent | CollapsedPairwiseNodeMouseMoveEvent,
  ) {
    if (!this.dragOrigin) {
      return;
    }

    const selectables = this.graphExplorer.getSelectables();
    const worldPoint = this.g6Wrapper.getPointByClient(e.raw.x, e.raw.y);

    for (const selectable of selectables) {
      const g6Node: G6.INode = this.g6Wrapper.nodeBySelectable(selectable) as unknown as G6.INode;
      const { x: nodeX, y: nodeY } = g6Node.get('model');

      g6Node.updatePosition({
        x: worldPoint.x - this.dragOrigin.x + nodeX,
        y: worldPoint.y - this.dragOrigin.y + nodeY,
      });

      const edges = g6Node.getEdges();
      for (const edge of edges) {
        edge.refresh();
      }

      this.throttledHoverCardReposition(g6Node);
    }

    this.dragOrigin = { x: worldPoint.x, y: worldPoint.y };
    this.throttledUpdateEvent();
  }

  private throttledHoverCardReposition = throttle(16, (g6Node: G6.INode) => {
    const { x, y } = g6Node.get('model');

    const g6Id = g6Node.getID();
    const hashableNodeId = g6IdToHashableNodeId(g6Id);
    if (hashableNodeId) {
      this.eventBus.dispatchEvent(
        new NodePositionChangeEvent(null, {
          [hashableNodeId]: { x, y },
        }),
      );
    }
    const collapsedGroupId = g6IdToCollapsedGroupId(g6Id);
    if (collapsedGroupId) {
      this.eventBus.dispatchEvent(
        new CollapsedGroupPositionChangeEvent(null, {
          [collapsedGroupId]: { x, y },
        }),
      );
    }
    const collapsedNodeId = g6IdToCollapsedPairwiseNodeId(g6Id);
    if (collapsedNodeId) {
      this.eventBus.dispatchEvent(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        new CollapsedPairwiseNodePositionChangeEvent(null, {
          [collapsedNodeId]: { x, y },
        }),
      );
    }
  });

  private throttledUpdateEvent = throttle(16, () => {
    if (this.g6Wrapper.selectionBox && !this.g6Wrapper.selectionBox.destroyed) {
      this.g6Wrapper.selectionBoxUpdateByCanvasSelectables(this.graphExplorer.getSelectables());
      this.eventBus.dispatchEvent(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        new SelectionBoxPositionChangeEvent(null, this.g6Wrapper.selectionBox.getBBox()),
      );
    }
  });

  handleNodeMouseUp() {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragOrigin = null;
  }

  handleCollapsedGroupMouseUp() {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragOrigin = null;
  }

  handleCollapsedPairwiseNodeMouseUp() {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragOrigin = null;
  }
}
